import { Box } from '@bonusly/design-system';

import type { BizyAssistantMessageNode } from '../../../schemas/bizy-conversations';
import type { BizyUiBlock } from '../../../schemas/bizy-ui-blocks';
import type { ApprovalDecision, CardNode } from '../hooks/use-bizy-global-chat';
import { UiBlockRenderer } from '../ui-blocks/ui-block-renderer';
import { BizyCardRenderer } from './bizy-card-renderer';
import { BizyStreamingMessage } from './bizy-streaming-message';

interface BizyDocumentRendererProps {
	fontSize?: 'sm' | 'md';
	/** Ordered sequence of typed nodes — markdown content interleaved with
	 *  card nodes. Walked top-to-bottom; markdown nodes render via
	 *  Streamdown, card nodes via BizyCardRenderer. */
	nodes: BizyAssistantMessageNode[];
	/** Whether the streaming turn is complete. Forwarded to the markdown
	 *  renderer so the in-progress styling (italic, pulsing cursor) clears
	 *  when the document is finalized. */
	isComplete?: boolean;
	/** Stable key for the message — used by card components to record per-card
	 *  user choices (approve / draft confirm). */
	messageKey?: string;
	/** Called when content updates (for auto-scroll). */
	onContentUpdate?: () => void;
	/** Tool progress labels shown while tools are running. Forwarded only to
	 *  the active (last) markdown node — the one currently receiving tokens. */
	toolProgress?: string[];
	/** Rotating thinking phrase. Forwarded only to the active (last) markdown
	 *  node — the one currently receiving tokens. */
	thinkingPhrase?: string;
	/** User choices keyed by the position of the interactive (card-typed)
	 *  node within the document. Lets the renderer show post-click state
	 *  (e.g., "Sent ✓") after a reload. */
	nodeChoices?: Record<number, ApprovalDecision>;
	/** Per-card callbacks — same contract as BizyCardRenderer. */
	onConfirm?: (cardIndex: number, confirmMessage: string) => void;
	onChoiceUpdate?: (messageKey: string, cardIndex: number, choice: ApprovalDecision) => void;
	onSuggestedPageClick?: (path: string) => void;
}

/**
 * Renders a typed AssistantMessage document.
 *
 * Walks the node array in order and dispatches per `type`:
 * - `markdown` → BizyStreamingMessage (Streamdown markdown renderer).
 * - `approval_card` / `content_card` / `draft_card` → BizyCardRenderer
 *   (per-card components for each card type).
 * - `recognition_draft_card` / `comment_draft_card` /
 *   `recognition_view_card` → typed UiBlockRenderer.
 *
 * `cardIndex` for card callbacks tracks the cumulative position of card
 * nodes across the document — a card emitted after multiple markdown
 * sections gets a unique index for choice tracking.
 */
export function BizyDocumentRenderer({
	fontSize,
	nodes,
	isComplete = false,
	messageKey,
	onContentUpdate,
	toolProgress,
	thinkingPhrase,
	nodeChoices,
	onConfirm,
	onChoiceUpdate,
	onSuggestedPageClick,
}: BizyDocumentRendererProps) {
	let cardCount = 0;
	// Index of the last markdown node — only this one receives streaming UI
	// (tool progress / thinking phrase / pulsing cursor) when the turn is
	// in flight. Earlier markdown nodes were closed by an intervening card
	// and are finalized segments even mid-stream.
	const lastMarkdownIdx = (() => {
		for (let i = nodes.length - 1; i >= 0; i -= 1) {
			if (nodes[i].type === 'markdown') return i;
		}
		return -1;
	})();

	return (
		<Box data-testid="bizy-document-renderer">
			{nodes.map((node, idx) => {
				if (node.type === 'markdown') {
					const isActiveMarkdown = idx === lastMarkdownIdx;
					const isFollowedByMarkdown = nodes[idx + 1]?.type === 'markdown';
					const isWorkingPlan = node.payload.role === 'working_plan';
					// Inactive markdown nodes are closed segments — render as
					// complete even when the document as a whole is still
					// streaming, so they don't show their own "Thinking..."
					// label or pulsing cursor.
					return (
						<Box key={`md-${idx}`} mb={isFollowedByMarkdown ? 3 : 0}>
							<BizyStreamingMessage
								content={node.payload.content}
								fontSize={fontSize}
								isComplete={isComplete || !isActiveMarkdown}
								onContentUpdate={onContentUpdate}
								toolProgress={isActiveMarkdown ? toolProgress : undefined}
								thinkingPhrase={isActiveMarkdown ? thinkingPhrase : undefined}
								progressPlacement={isWorkingPlan ? 'below' : 'above'}
								onSuggestedPageClick={onSuggestedPageClick}
							/>
						</Box>
					);
				}

				if (node.type === 'recognition_draft_card' || node.type === 'comment_draft_card' || node.type === 'recognition_view_card') {
					return <UiBlockRenderer key={`card-${idx}`} block={node as BizyUiBlock} fontSize={fontSize} />;
				}

				// Legacy card node — wrap as a single-element CardNode array for
				// the per-card dispatcher. cardIndexOffset gives the card a
				// stable index across the document so onChoiceUpdate
				// (messageKey, cardIndex, choice) targets the right card.
				const offset = cardCount;
				cardCount += 1;
				const card: CardNode = {
					type: node.type,
					payload: node.payload,
					choice: nodeChoices?.[offset],
				};
				return (
					<BizyCardRenderer
						key={`card-${idx}`}
						cards={[card]}
						messageKey={messageKey}
						cardIndexOffset={offset}
						onConfirm={onConfirm}
						onChoiceUpdate={onChoiceUpdate}
					/>
				);
			})}
		</Box>
	);
}
